import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';
import { useApp } from '../../context/AppContext';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import useHandleErrors from '../../hooks/useHandleErrors';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent, { ErrorContent } from '../../components/LoadingContent';
import { useConfirm } from 'material-ui-confirm';
import SelectTopics from '../../components/SelectTopics';
import RescheduleDialog from './components/RescheduleDialog';
import CancelScheduleDialog from './components/CancelScheduleDialog';
import { getServerNow, useServerTime } from '../../hooks/useServerTime';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BlockIcon from '@mui/icons-material/Block';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { precoMinimo } from '../../global/repasse';

const SectionCard = ({ children }) => (
    <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        p: { xs: '20px 16px', sm: '28px 24px' },
    }}>
        {children}
    </Box>
);

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" fontWeight={600} color="#333" sx={{ mb: 2.5 }}>
        {children}
    </Typography>
);

export default () => {
    const { user, rules } = useAuth();
    const { isOffline } = useApp();
    const [hasError, setError] = useState();
    useHandleErrors(hasError);
    const params = useParams();
    const navigation = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    useServerTime();

    const [reagendando, setReagendando] = useState(null);
    const [cancelando, setCancelando] = useState(null);
    // Escolha de qual horario cancelar: so entra em cena quando ha mais de um
    const [escolhendoCancelamento, setEscolhendoCancelamento] = useState(false);
    const [horarioEscolhido, setHorarioEscolhido] = useState('');
    const [isFreeEvent, setIsFreeEvent] = useState(false);
    const [previousPricing, setPreviousPricing] = useState(0);
    const [pricingDisplay, setPricingDisplay] = useState('0,00');

    const { data, error, mutate, isLoading } = useFetch('/profile/meeting', { id: params.id });
    useHandleErrors(error);

    const meeting = Array.isArray(data) ? data.find(m => m.id === Number(params.id)) : data;

    // Verificar se tem compras
    const hasOrders = meeting?.meeting_schedule?.some(
        s => Array.isArray(s.Order) ? s.Order.length > 0 : Boolean(s.Order)
    );

    const formik = useFormik({
        initialValues: {
            id: '',
            title: '',
            description: '',
            status: true,
            visible_public: false,
            raffle: false,
            pricing: 0.0,
            free: false,
            topics: [],
        },
        enableReinitialize: false,
        validationSchema: Yup.object().shape({
            title: Yup.string().required('Campo obrigatório').max(40, 'Máximo 40 caracteres'),
            description: Yup.string().required('Campo obrigatório').max(350, 'Máximo 350 caracteres'),
            pricing: isFreeEvent
                ? Yup.mixed().notRequired()
                : Yup.number().min(precoMinimo(rules), `Preco minimo R$ ${Number(precoMinimo(rules)).toFixed(2).replace('.', ',')}`).required('Campo obrigatório'),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const payload = {
                    ...values,
                    visible_public: values.visible_public === true || values.visible_public === 'true',
                    raffle: values.raffle === true || values.raffle === 'true',
                    free: isFreeEvent,
                    pricing: isFreeEvent ? 0.0 : values.pricing,
                };
                await server.put('/profile/meeting', payload);
                await mutate();
                enqueueSnackbar('Encontro atualizado!');
                setSubmitting(false);
            } catch (error) {
                setSubmitting(false);
                setError(error);
            }
        },
    });

    const isRaffle = formik.values.raffle === true || formik.values.raffle === 'true';

    // Carregar dados do meeting
    useEffect(() => {
        if (meeting) {
            const meetingTopics = meeting.topics?.map(t => t.topic_id) || [];
            formik.setValues({
                id: meeting.id,
                title: meeting.title || '',
                description: meeting.description || '',
                status: meeting.status ?? true,
                visible_public: meeting.visible_public ?? false,
                raffle: meeting.raffle ?? false,
                pricing: meeting.free ? 0 : meeting.pricing || 0,
                free: Boolean(meeting.free),
                topics: meetingTopics,
            });
            setIsFreeEvent(Boolean(meeting.free));
            if (!meeting.free && meeting.pricing) {
                setPricingDisplay(Number(meeting.pricing).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                setPreviousPricing(meeting.pricing);
            }
        }
    }, [meeting]);

    // Pricing reset quando gratuito
    useEffect(() => {
        if (isFreeEvent) {
            setPreviousPricing(formik.values.pricing);
            formik.setFieldValue('pricing', 0.0);
            setPricingDisplay('0,00');
        } else {
            formik.setFieldValue('pricing', previousPricing);
            setPricingDisplay(previousPricing.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        }
    }, [isFreeEvent]);

    // Sorteio = sempre publico, nunca gratuito
    useEffect(() => {
        if (isRaffle) {
            formik.setFieldValue('visible_public', true);
            if (isFreeEvent) setIsFreeEvent(false);
        }
    }, [isRaffle]);

    const handlePricingChange = (e) => {
        let raw = e.target.value.replace(/\D/g, '');
        if (raw === '') raw = '0';
        const cents = parseInt(raw, 10);
        const reais = cents / 100;
        setPricingDisplay(reais.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        formik.setFieldValue('pricing', reais);
    };

    const handleCancel = async () => {
        try {
            await confirm({
                title: 'Cancelar encontro',
                description: 'Tem certeza que deseja cancelar este encontro? Esta ação é irreversível.',
            });
        } catch { return; }

        try {
            await server.delete(`/profile/meeting/${params.id}`);
            enqueueSnackbar('Encontro cancelado', { variant: 'success' });
            navigation('/meus-encontros', { replace: true });
        } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao cancelar encontro';
            enqueueSnackbar(msg, { variant: 'error' });
        }
    };

    const formatarMomento = valor =>
        valor ? new Date(valor).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

    // Horarios que ainda aceitam acao. Cancelado, encerrado e o que ja comecou
    // ficam de fora: neles nao ha o que remarcar nem cancelar. Os prazos (24h
    // para remarcar, nenhum para cancelar) quem decide e o servidor; os
    // dialogos so antecipam o aviso.
    const horariosEditaveis = (meeting?.meeting_schedule || [])
        .filter(s => s.datetime && !s.canceled_at && !s.finished_at && new Date(s.datetime) > getServerNow())
        .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

    const CardHorarios = () => {
        if (horariosEditaveis.length === 0) return null;

        return (
            <SectionCard>
                <SectionTitle>Horários</SectionTitle>
                <Stack spacing={1}>
                    {horariosEditaveis.map(item => {
                        const convivas = Array.isArray(item.Order)
                            ? item.Order.filter(o => ['CONCLUDED', 'PENDING'].includes(o.status)).length
                            : 0;

                        return (
                            <Stack
                                key={item.id}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                // A linha inteira abre a troca de horario: procurar o icone
                                // certo para uma acao que e a principal da linha e atrito a toa
                                onClick={() => setReagendando(item)}
                                sx={{
                                    border: '1px solid #eee',
                                    borderRadius: '12px',
                                    px: 1.5,
                                    py: 1,
                                    cursor: 'pointer',
                                    transition: 'background-color 0.15s, border-color 0.15s',
                                    '&:hover': { bgcolor: '#f7fbfa', borderColor: '#006a61' },
                                }}
                            >
                                <CalendarTodayIcon sx={{ fontSize: 16, color: '#888' }} />
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    {formatarMomento(item.datetime)}
                                </Typography>
                                {convivas > 0 && (
                                    <Chip
                                        label={convivas === 1 ? 'Vendido' : `${convivas} convivas`}
                                        size="small"
                                        sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500, fontSize: '0.7rem' }}
                                    />
                                )}
                                <Tooltip
                                    title={convivas > 0 ? 'Alterar horário (os convivas serão avisados)' : 'Alterar horário'}
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setReagendando(item);
                                        }}
                                        sx={{ color: '#006a61' }}
                                    >
                                        <EditCalendarIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        );
                    })}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    Clique em um horário para trocar a data. Se alguém já comprou, a troca exige 24h de
                    antecedência — os convivas são avisados e podem cancelar com reembolso integral.
                </Typography>
            </SectionCard>
        );
    };

    // Com um horario so nao ha o que perguntar; com varios, o botao do rodape
    // nao tem como adivinhar qual, entao abre a lista antes
    const iniciarCancelamento = () => {
        if (horariosEditaveis.length === 1) {
            setCancelando(horariosEditaveis[0]);
            return;
        }
        setHorarioEscolhido(String(horariosEditaveis[0]?.id ?? ''));
        setEscolhendoCancelamento(true);
    };

    const confirmarHorarioEscolhido = () => {
        const item = horariosEditaveis.find(s => String(s.id) === String(horarioEscolhido));
        if (!item) return;
        setEscolhendoCancelamento(false);
        setCancelando(item);
    };

    const BackToCalendar = () => (
        <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', px: { xs: '12px', sm: '16px' }, pt: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigation('/calendario')} sx={{ textTransform: 'none', color: '#666' }}>
                Calendário
            </Button>
        </Box>
    );

    if (isLoading) return <><BackToCalendar /><LoadingContent /></>;
    if (error) return <><BackToCalendar /><ErrorContent label={error?.response?.data?.message} /></>;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 3,
        }}>
            {/* Voltar */}
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigation('/calendario')} sx={{ textTransform: 'none', color: '#666' }}>
                    Calendário
                </Button>
            </Box>

            {/* Header */}
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: hasOrders ? '#f5f5f5' : '#006a61' }}>
                    {hasOrders
                        ? <BlockIcon sx={{ fontSize: 32, color: '#999' }} />
                        : <EditIcon sx={{ fontSize: 32 }} />
                    }
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                    {hasOrders ? 'Detalhes do encontro' : 'Editar encontro'}
                </Typography>
                {hasOrders && (
                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        Alguém já adquiriu este encontro. Título, descrição e valor ficam como estão — o
                        horário você ainda pode ajustar aqui embaixo.
                    </Alert>
                )}
            </Box>

            {user?.profile && !user.profile.approved && (
                <Alert severity="warning" sx={{ width: '100%', borderRadius: '12px', '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                    Seu cadastro de anfitrião está em análise. Este encontro só ficará visível para outros usuários após a aprovação.
                </Alert>
            )}

            {hasOrders ? (
                <>
                    {/* Modo somente leitura */}
                    <SectionCard>
                        <SectionTitle>Informações do encontro</SectionTitle>
                        <Stack spacing={2}>
                            <TextField label="Título" size="small" fullWidth value={meeting?.title || ''} disabled />
                            <TextField label="Descrição" size="small" fullWidth multiline minRows={3} value={meeting?.description || ''} disabled />
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Typography variant="body2" color="text.secondary">
                                    {meeting?.raffle ? 'Sorteio' : 'Agendamento'} • {meeting?.visible_public ? 'Publico' : 'Privado'} • {meeting?.free ? 'Gratuito' : `R$ ${Number(meeting?.pricing).toFixed(2).replace('.', ',')}`}
                                </Typography>
                            </Stack>
                        </Stack>
                    </SectionCard>

                    <CardHorarios />

                    {/* Sem horario em aberto nao ha o que cancelar: "Acompanhar" fica sozinho */}
                    <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => navigation(`/meus-encontros/${params.id}/horarios`)}
                            sx={{
                                borderRadius: '8px',
                                height: 48,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '1rem',
                                borderColor: '#006a61',
                                color: '#006a61',
                                '&:hover': { bgcolor: '#f0faf9', borderColor: '#005a52' },
                            }}
                        >
                            Acompanhar encontro
                        </Button>
                        {horariosEditaveis.length > 0 && (
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={iniciarCancelamento}
                                startIcon={<EventBusyIcon />}
                                sx={{
                                    borderRadius: '8px',
                                    height: 48,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    bgcolor: '#c62828',
                                    '&:hover': { bgcolor: '#a91d1d' },
                                }}
                            >
                                Cancelar encontro
                            </Button>
                        )}
                    </Stack>
                </>
            ) : (
                /* Modo edicao completa */
                <form onSubmit={formik.handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}>

                    {/* Informacoes basicas */}
                    <SectionCard>
                        <SectionTitle>Informações do encontro</SectionTitle>
                        <Stack spacing={2}>
                            <TextField
                                label="Título do encontro"
                                name="title"
                                size="small"
                                fullWidth
                                required
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={isOffline}
                                error={formik.touched.title && Boolean(formik.errors.title)}
                                helperText={formik.touched.title ? formik.errors.title : `${40 - (formik.values.title?.length || 0)} caracteres restantes`}
                                inputProps={{ maxLength: 40 }}
                            />
                            <TextField
                                label="Descrição do encontro"
                                name="description"
                                size="small"
                                fullWidth
                                required
                                multiline
                                minRows={3}
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                disabled={isOffline}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={formik.touched.description ? formik.errors.description : `${350 - (formik.values.description?.length || 0)} caracteres restantes`}
                                inputProps={{ maxLength: 350 }}
                            />
                            <SelectTopics
                                onChange={formik.setFieldValue}
                                name="topics"
                                value={formik.values.topics}
                            />
                        </Stack>
                    </SectionCard>

                    {/* Tipo e visibilidade */}
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2.5 }}>
                            <Typography variant="subtitle1" fontWeight={600} color="#333">
                                Tipo e visibilidade
                            </Typography>
                            <Tooltip
                                arrow
                                title={
                                    <Box sx={{ p: 0.5 }}>
                                        <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>Público</Typography>
                                        <Typography variant="caption" display="block">Qualquer usuário pode encontrar e comprar. Conta com transmissão ao vivo.</Typography>
                                        <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 1, mb: 0.5 }}>Privado</Typography>
                                        <Typography variant="caption" display="block">Qualquer usuário pode encontrar e comprar, porém sem transmissão ao vivo.</Typography>
                                    </Box>
                                }
                            >
                                <HelpOutlineIcon sx={{ fontSize: 18, color: '#999', cursor: 'help' }} />
                            </Tooltip>
                        </Box>
                        <Stack spacing={2.5}>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Modalidade</Typography>
                                <ToggleButtonGroup
                                    value={isRaffle ? 'raffle' : 'schedule'}
                                    exclusive
                                    onChange={(e, val) => { if (val) formik.setFieldValue('raffle', val === 'raffle'); }}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500 },
                                        '& .Mui-selected': { bgcolor: '#e6f3f1 !important', color: '#006a61 !important', borderColor: '#006a61 !important' },
                                    }}
                                >
                                    <ToggleButton value="schedule">Agendamento</ToggleButton>
                                    <ToggleButton value="raffle">Sorteio</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Visibilidade</Typography>
                                <ToggleButtonGroup
                                    value={formik.values.visible_public === true || formik.values.visible_public === 'true' ? 'public' : 'private'}
                                    exclusive
                                    onChange={(e, val) => { if (val) formik.setFieldValue('visible_public', val === 'public'); }}
                                    fullWidth
                                    size="small"
                                    disabled={isRaffle}
                                    sx={{
                                        '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500 },
                                        '& .Mui-selected': { bgcolor: '#e6f3f1 !important', color: '#006a61 !important', borderColor: '#006a61 !important' },
                                    }}
                                >
                                    <ToggleButton value="public">Público</ToggleButton>
                                    <ToggleButton value="private">Privado</ToggleButton>
                                </ToggleButtonGroup>
                                {isRaffle && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        Sorteios são sempre públicos
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </SectionCard>

                    {/* Valor */}
                    <SectionCard>
                        <SectionTitle>Valor</SectionTitle>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Encontro gratuito</Typography>
                                <Switch
                                    checked={isFreeEvent}
                                    onChange={() => setIsFreeEvent(!isFreeEvent)}
                                    disabled={isRaffle}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#006a61' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#006a61' },
                                    }}
                                />
                            </Box>
                            <TextField
                                label="Valor"
                                size="small"
                                fullWidth
                                value={isFreeEvent ? '0,00' : pricingDisplay}
                                onChange={handlePricingChange}
                                onBlur={() => formik.setFieldTouched('pricing', true)}
                                disabled={isFreeEvent || isOffline}
                                error={formik.touched.pricing && Boolean(formik.errors.pricing)}
                                helperText={
                                    isFreeEvent
                                        ? 'Este encontro será gratuito'
                                        : formik.touched.pricing && formik.errors.pricing
                                            ? formik.errors.pricing
                                            : `Valor minimo R$ ${Number(precoMinimo(rules)).toFixed(2).replace('.', ',')}`
                                }
                                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                                inputProps={{ inputMode: 'numeric' }}
                            />
                        </Stack>
                    </SectionCard>

                    <CardHorarios />

                    {/* Botoes */}
                    <Stack spacing={1.5}>
                        <LoadingButton
                            type="submit"
                            fullWidth
                            loading={formik.isSubmitting}
                            variant="contained"
                            disabled={formik.isSubmitting || isOffline}
                            sx={{
                                bgcolor: '#006a61',
                                borderRadius: '8px',
                                height: 48,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '1rem',
                                '&:hover': { bgcolor: '#005a52' },
                            }}
                        >
                            Salvar alterações
                        </LoadingButton>
                        <Button
                            variant="outlined"
                            fullWidth
                            color="error"
                            onClick={handleCancel}
                            sx={{
                                borderRadius: '8px',
                                height: 44,
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            Cancelar encontro
                        </Button>
                    </Stack>
                </form>
            )}

            {/* Troca de horario de um slot ja agendado */}
            <RescheduleDialog
                open={Boolean(reagendando)}
                schedule={reagendando}
                onClose={() => setReagendando(null)}
                onSaved={mutate}
            />

            {/* Qual horario cancelar. Só aparece quando ha mais de um: com um
                so, o botao do rodape vai direto para o dialogo de cancelamento */}
            <Dialog
                open={escolhendoCancelamento}
                onClose={() => setEscolhendoCancelamento(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Cancelar qual horário?</DialogTitle>
                <DialogContent>
                    <RadioGroup value={horarioEscolhido} onChange={e => setHorarioEscolhido(e.target.value)}>
                        {horariosEditaveis.map(item => {
                            const convivas = Array.isArray(item.Order)
                                ? item.Order.filter(o => ['CONCLUDED', 'PENDING'].includes(o.status)).length
                                : 0;

                            return (
                                <FormControlLabel
                                    key={item.id}
                                    value={String(item.id)}
                                    control={<Radio sx={{ '&.Mui-checked': { color: '#c62828' } }} />}
                                    label={
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body2">{formatarMomento(item.datetime)}</Typography>
                                            {convivas > 0 && (
                                                <Chip
                                                    label={convivas === 1 ? 'Vendido' : `${convivas} convivas`}
                                                    size="small"
                                                    sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500, fontSize: '0.7rem' }}
                                                />
                                            )}
                                        </Stack>
                                    }
                                />
                            );
                        })}
                    </RadioGroup>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEscolhendoCancelamento(false)} sx={{ textTransform: 'none', color: '#666' }}>
                        Voltar
                    </Button>
                    <Button
                        onClick={confirmarHorarioEscolhido}
                        disabled={!horarioEscolhido}
                        variant="contained"
                        color="error"
                        sx={{ textTransform: 'none' }}
                    >
                        Continuar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancelamento de um horario, inclusive com comprador */}
            <CancelScheduleDialog
                open={Boolean(cancelando)}
                schedule={cancelando}
                gratuito={Boolean(meeting?.free)}
                onClose={() => setCancelando(null)}
                onCanceled={mutate}
            />
        </Box>
    );
};
